import React, { useEffect, useState } from "react";
import styles from "./home.module.css";
import { useNavigate } from "react-router-dom";
import ContainerOptions from "common/ContainerOptions";
import ContainerHeader from "common/ContainerHeader";
import DataTable_ from "common/DataTable";
import Metric from "components/Home/Metric";

import { GetOrderVolume } from "pages/Analytics/testFields";
import EmptyPage from "common/EmptyPage";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import { getAll } from "utils/queries";
import { formatDateFromDateAndTime } from "utils";
import PieDiagram from "components/Home/PieDiagram";
import DateFilterDropdown from "common/EnhancedDatePicker";
import ComparisonGraph from "components/Home/Graph";
import SelectSearch from "common/SelectSearch";
import { SearchOptions } from "utils/queries";

const Home = () => {
  const navigate = useNavigate();
  const auth = useAuthUser();

  const [priceByDate, setPriceByDate] = useState({
    xAxisData: [],
    yAxisData: [[]],
  });
  const [comparisonData, setComparisonData] = useState({
    xAxisData: [],
    yAxisData: [[]],
  });
  const [showComparison, setShowComparison] = useState(false);
  const [revenueAndOrders, setRevenueAndOrders] = useState([]);
  const [dailySales, setDailySales] = useState([]);
  const [pendingRevenue, setPendingRevenue] = useState([]);
  const [salesByDate, setSalesByDate] = useState([]);
  const [conversions, setConversions] = useState([]);
  const [ticketSales, setTicket] = useState([]);
  const [sessions, setSessions] = useState({ total_sessions: 0, conversion_rate: 0 });
  const [conversionRate, setConversionRate] = useState(0);
  const [dateLabel, setDateLabel] = useState("Last 7 Days");
  const [comparisonPeriod, setComparisonPeriod] = useState("Previous Period");
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Add state variables to track current date range
  const [currentStartDate, setCurrentStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 6);
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [currentEndDate, setCurrentEndDate] = useState(() => {
    const d = new Date();
    d.setHours(23, 59, 59, 999);
    return d;
  });
  const [totalSales, setTotalSales] = useState([]);
  const [totalSalesCount, setTotalSalesCount] = useState([]);
  const [ticketSalesBreakDown, setTicketSalesBreakDown] = useState([]);
  const [ticketSalesBreakDownCount, setTicketSalesBreakDownCount] = useState(
    []
  );

  // New state for multi-metric data
  const [allMetricsData, setAllMetricsData] = useState({});
  const [allMetricsComparisonData, setAllMetricsComparisonData] = useState({});
  const [selectedMetrics, setSelectedMetrics] = useState(["revenue"]); // Revenue shown by default

  // Helper function to create date-indexed map for consistent x-axis alignment
  const createDateIndexedData = (dateArray, dataArray, valueKey) => {
    const dateMap = {};
    dataArray.forEach((item) => {
      const date = formatDateFromDateAndTime(item.date || item.created_at);
      dateMap[date] = item[valueKey];
    });

    return dateArray.map((date) => dateMap[date] || 0);
  };

  // Main data fetching function
  const getPriceByDate = async (startDate, endDate, days) => {
    try {
      // Format dates for API
      const formattedStartDate = startDate.toISOString().split("T")[0];
      const formattedEndDate = endDate.toISOString().split("T")[0];

      let query = `orders/metrics/getTotalRevenueByDate?date=${formattedStartDate}&endDate=${formattedEndDate}`;
      if (selectedEvent?.value) {
        query += `&event_id=${selectedEvent.value}`;
      }

      const priceByDate_ = await getAll(query);

      console.log("data is", priceByDate_?.data?.data);

      const apiData = priceByDate_?.data?.data;
      console.log("API Data:", apiData);
      console.log("pendingRevenueByDate:", apiData?.pendingRevenueByDate);
      if (apiData?.pendingRevenueByDate?.length > 0) {
        console.log("pendingRevenueByDate first item keys:", Object.keys(apiData.pendingRevenueByDate[0]));
        console.log("pendingRevenueByDate first item:", apiData.pendingRevenueByDate[0]);
      }

      // Create consistent x-axis data from ALL available date sources
      // This ensures graphs still display even if revenueByDate is empty
      const dateSet = new Set();
      (apiData?.revenueByDate || []).forEach(e => {
        const d = formatDateFromDateAndTime(e?.created_at);
        if (d) dateSet.add(d);
      });
      (apiData?.salesByDate || []).forEach(e => {
        const d = formatDateFromDateAndTime(e?.date || e?.created_at);
        if (d) dateSet.add(d);
      });
      (apiData?.totalTicketsSold || []).forEach(e => {
        const d = formatDateFromDateAndTime(e?.date || e?.created_at);
        if (d) dateSet.add(d);
      });
      (apiData?.pendingRevenueByDate || []).forEach(e => {
        const d = formatDateFromDateAndTime(e?.date || e?.created_at);
        if (d) dateSet.add(d);
      });
      const xAxisData = Array.from(dateSet).sort((a, b) => 
        new Date(a) - new Date(b)
      );

      // Store all metrics data using real API data
      const metricsData = {
        revenue: {
          xAxisData: xAxisData,
          yAxisData:
            apiData?.revenueByDate?.map((e) =>
              parseFloat(e?.total_revenue_by_date)
            ) || [],
        },
        pendingRevenue: {
          xAxisData: apiData?.pendingRevenueByDate?.map((e) =>
            formatDateFromDateAndTime(e?.created_at)
          ) || xAxisData,
          yAxisData:
            apiData?.pendingRevenueByDate?.map((e) =>
              parseFloat(e?.total_revenue_by_date)
            ) || [],
        },
        sessions: {
          xAxisData: apiData?.ga_graph?.map(item => item.date) || [],
          yAxisData: apiData?.ga_graph?.map(item => item.sessions || 0) || [],
        },
        orders: {
          xAxisData: xAxisData,
          yAxisData: createDateIndexedData(
            xAxisData,
            apiData?.salesByDate || [],
            "total_sales_by_date"
          ).map((val) => parseInt(val)),
        },
        tickets: {
          xAxisData: xAxisData,
          yAxisData: createDateIndexedData(
            xAxisData,
            apiData?.totalTicketsSold || [],
            "total_tickets_sold"
          ).map((val) => parseInt(val)),
        },
        //          conversion: {
        //     xAxisData: xAxisData,
        //     // Conversion rate is typically a single value, so we'll use it for all dates
        //     // In a real scenario, you'd want conversion rate by date from the API
        //     yAxisData: xAxisData.map(() => {
        //       const conversionRate = parseFloat(apiData?.conversionResult?.[0]?.conversion_rate || 0);
        //       return conversionRate;
        //     }),
        //   },
        // };
        conversion: {
          xAxisData: apiData?.ga_graph?.map(item => item.date) || [],
          yAxisData: apiData?.ga_graph?.map(item => item.conversionRate || 0) || [],
        },
      };

      setAllMetricsData(metricsData);

      // Keep existing revenue data for backward compatibility
      setPriceByDate({
        xAxisData: metricsData.revenue.xAxisData,
        yAxisData: [metricsData.revenue.yAxisData],
      });

      setRevenueAndOrders(apiData?.totalSalesAndRevenue?.[0]);
      setDailySales(apiData?.revenueByDate?.[0]);
      setPendingRevenue(apiData?.pendingRevenueByDate?.[0]);
      setSalesByDate(apiData?.salesByDate?.[0]);
      setConversions(apiData?.conversionResult?.[0]);
      setTicket(apiData?.totalTicketsSold?.[0]);
      setSessions({
        total_sessions: apiData.ga_summary?.total_sessions || 0,
        conversion_rate: apiData.ga_summary?.total_conversion_rate || 0
      });
      setConversionRate(apiData.ga_summary?.total_conversion_rate || 0);
    } catch (error) {
      console.error("Error fetching price by date:", error);
    }
  };

  // Calculate comparison date range based on period type
  const calculateComparisonDateRange = (startDate, endDate, periodType) => {
    const days = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
    let comparisonStartDate, comparisonEndDate;

    if (periodType === "Previous Period") {
      comparisonStartDate = new Date(startDate);
      comparisonEndDate = new Date(endDate);
      comparisonStartDate.setDate(comparisonStartDate.getDate() - days);
      comparisonEndDate.setDate(comparisonEndDate.getDate() - days);
    } else if (periodType === "Previous Year") {
      comparisonStartDate = new Date(startDate);
      comparisonEndDate = new Date(endDate);
      comparisonStartDate.setFullYear(comparisonStartDate.getFullYear() - 1);
      comparisonEndDate.setFullYear(comparisonEndDate.getFullYear() - 1);
    } else if (periodType === "Previous Week") {
      comparisonStartDate = new Date(startDate);
      comparisonEndDate = new Date(endDate);
      comparisonStartDate.setDate(comparisonStartDate.getDate() - 7);
      comparisonEndDate.setDate(comparisonEndDate.getDate() - 7);
    } else if (periodType === "Previous Month") {
      comparisonStartDate = new Date(startDate);
      comparisonEndDate = new Date(endDate);
      comparisonStartDate.setMonth(comparisonStartDate.getMonth() - 1);
      comparisonEndDate.setMonth(comparisonEndDate.getMonth() - 1);

      const lastDayOfMonth = new Date(
        comparisonEndDate.getFullYear(),
        comparisonEndDate.getMonth() + 1,
        0
      ).getDate();
      if (comparisonEndDate.getDate() > lastDayOfMonth) {
        comparisonEndDate.setDate(lastDayOfMonth);
      }
    } else {
      comparisonStartDate = new Date(startDate);
      comparisonEndDate = new Date(endDate);
      comparisonStartDate.setDate(comparisonStartDate.getDate() - days);
      comparisonEndDate.setDate(comparisonEndDate.getDate() - days);
    }

    return { comparisonStartDate, comparisonEndDate };
  };

  // Fetch comparison data
  const fetchComparisonData = async (startDate, endDate, period) => {
    try {
      const { comparisonStartDate, comparisonEndDate } =
        calculateComparisonDateRange(startDate, endDate, period);

      const formattedStartDate = comparisonStartDate
        .toISOString()
        .split("T")[0];
      const formattedEndDate = comparisonEndDate.toISOString().split("T")[0];

      let query = `orders/metrics/getTotalRevenueByDate?date=${formattedStartDate}&endDate=${formattedEndDate}`;
      if (selectedEvent?.value) {
        query += `&event_id=${selectedEvent.value}`;
      }

      const comparisonData_ = await getAll(query);

      const apiData = comparisonData_?.data?.data;

      // Create consistent x-axis data from ALL available date sources
      const compDateSet = new Set();
      (apiData?.revenueByDate || []).forEach(e => {
        const d = formatDateFromDateAndTime(e?.created_at);
        if (d) compDateSet.add(d);
      });
      (apiData?.salesByDate || []).forEach(e => {
        const d = formatDateFromDateAndTime(e?.date || e?.created_at);
        if (d) compDateSet.add(d);
      });
      (apiData?.totalTicketsSold || []).forEach(e => {
        const d = formatDateFromDateAndTime(e?.date || e?.created_at);
        if (d) compDateSet.add(d);
      });
      (apiData?.pendingRevenueByDate || []).forEach(e => {
        const d = formatDateFromDateAndTime(e?.date || e?.created_at);
        if (d) compDateSet.add(d);
      });
      const xAxisData = Array.from(compDateSet).sort((a, b) =>
        new Date(a) - new Date(b)
      );

      // Store comparison data for all metrics using real API data
      const comparisonMetricsData = {
        revenue: {
          xAxisData: xAxisData,
          yAxisData:
            apiData?.revenueByDate?.map((e) =>
              parseFloat(e?.total_revenue_by_date)
            ) || [],
        },
        pendingRevenue: {
          xAxisData: apiData?.pendingRevenueByDate?.map((e) =>
            formatDateFromDateAndTime(e?.created_at)
          ) || xAxisData,
          yAxisData:
            apiData?.pendingRevenueByDate?.map((e) =>
              parseFloat(e?.total_revenue_by_date)
            ) || [],
        },
        sessions: {
          xAxisData: xAxisData,
          yAxisData: xAxisData.map(() => {
            const totalSessions = parseInt(
              apiData?.totalSessions?.[0]?.total_sessions || 0
            );
            return Math.floor(totalSessions / xAxisData.length);
          }),
        },
        orders: {
          xAxisData: xAxisData,
          yAxisData: createDateIndexedData(
            xAxisData,
            apiData?.salesByDate || [],
            "total_sales_by_date"
          ).map((val) => parseInt(val)),
        },
        tickets: {
          xAxisData: xAxisData,
          yAxisData: createDateIndexedData(
            xAxisData,
            apiData?.totalTicketsSold || [],
            "total_tickets_sold"
          ).map((val) => parseInt(val)),
        },
        //   conversion: {
        //     xAxisData: xAxisData,
        //     yAxisData: xAxisData.map(() => {
        //       const conversionRate = parseFloat(apiData?.conversionResult?.[0]?.conversion_rate || 0);
        //       return conversionRate;
        //     }),
        //   },
        // };
        conversion: {
          xAxisData: xAxisData,
          yAxisData: createDateIndexedData(
            xAxisData,
            apiData?.conversionResult || [],
            "conversion_rate"
          ).map((val) => parseFloat(val)),
        },
      };

      setAllMetricsComparisonData(comparisonMetricsData);

      // Keep existing comparison data for backward compatibility
      setComparisonData({
        xAxisData: comparisonMetricsData.revenue.xAxisData,
        yAxisData: [comparisonMetricsData.revenue.yAxisData],
      });

      let displayPeriod = period;
      if (period === "Previous Period" || period === "Previous Year") {
        const formatDate = (date) => {
          return date.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "2-digit",
          });
        };

        displayPeriod = `${period} (${formatDate(
          comparisonStartDate
        )} - ${formatDate(comparisonEndDate)})`;
      }

      setComparisonPeriod(displayPeriod);
    } catch (error) {
      console.error("Error fetching comparison data:", error);
    }
  };

  // Handle metric selection
  const handleMetricClick = (metricKey) => {
    setSelectedMetrics((prev) => {
      if (prev.includes(metricKey)) {
        // Remove metric if already selected
        return prev.filter((key) => key !== metricKey);
      } else {
        // Add metric if not selected
        return [...prev, metricKey];
      }
    });
  };

  // Helper function to format date label
  const formatDateRangeLabel = (startDate, endDate, rangeType) => {
    if (!startDate || !endDate) return rangeType || "Date Range";

    if (rangeType && rangeType !== "Custom") {
      return rangeType;
    }

    const start = startDate.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
    });
    const end = endDate.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
    });
    return `${start} - ${end}`;
  };

  // Handle date range change from the dropdown
  const handleDateRangeChange = (
    startDate,
    endDate,
    days,
    comparisonPeriodValue
  ) => {
    console.log(
      `Selected date range: ${startDate.toDateString()} to ${endDate.toDateString()}`
    );

    setCurrentStartDate(startDate);
    setCurrentEndDate(endDate);

    if (comparisonPeriodValue) {
      setComparisonPeriod(comparisonPeriodValue);
    }

    setDateLabel(
      formatDateRangeLabel(
        startDate,
        endDate,
        days ? `Last ${days} days` : "Custom"
      )
    );
  };

  // Handle comparison toggle
  const handleToggleComparison = (enabled) => {
    setShowComparison(enabled);
    if (enabled && currentStartDate && currentEndDate) {
      fetchComparisonData(
        currentStartDate,
        currentEndDate,
        comparisonPeriod || "Previous Period"
      );
    }
  };

  // Initial data loading and reactive updates
  useEffect(() => {
    if (currentStartDate && currentEndDate) {
      getPriceByDate(currentStartDate, currentEndDate);
      fetchTicketSalesBreakDown({
        startDate: currentStartDate,
        endDate: currentEndDate,
      });
      fetchComparisonData(
        currentStartDate,
        currentEndDate,
        comparisonPeriod || "Previous Period"
      );
    }
  }, [currentStartDate, currentEndDate, selectedEvent?.value, comparisonPeriod]);

  const fetchOptions = async (search = "") => {
    try {
      let optionsResult;
      
      if (!search || search.trim() === "") {
        // Load all events (past and upcoming) when no search term
        // Removing eventType=upcoming to ensure past events with data are visible
        optionsResult = await SearchOptions(`event/events?limit=100&offset=0`);
        return optionsResult?.data?.data?.rows?.map((e) => ({
          value: e?.id,
          label: e?.event_name,
        })) || [];
      } else {
        // Use search endpoint when user types
        optionsResult = await SearchOptions(`ticket/search?key=${search}`);
        return optionsResult?.data?.data
          ?.filter((e) =>
            e?.event_name?.toLowerCase().includes(search.toLowerCase())
          )
          ?.map((e) => ({ value: e?.id, label: e?.event_name })) || [];
      }
    } catch (error) {
      console.error("Error fetching options:", error);
      return [];
    }
  };

  const columns = [
    {
      name: "Date",
      selector: (row) => formatDateFromDateAndTime(row.date),
    },
    {
      name: "Orders",
      selector: (row) => row.total_sales,
    },
    {
      name: "Referrals",
      selector: (row) => row.referrals,
    },
    {
      name: "Money In",
      selector: (row) => `£ ${row.total_in}`,
    },
    {
      name: "Money Out ",
      selector: (row) => `£ ${row.total_cut}`,
    },
    {
      name: "Revenue",
      selector: (row) => row.revenue,
    },
  ];

  const fetchTicketSalesBreakDown = async ({
    limit = 10,
    offset = 0,
    startDate,
    endDate,
  }) => {
    try {
      const formattedStartDate = startDate?.toISOString().split("T")[0];
      const formattedEndDate = endDate?.toISOString().split("T")[0];
      let data = `orders/metrics/ticketsales/daily?date=${formattedStartDate}&endDate=${formattedEndDate}&limit=${limit}&offset=${offset}`;

      if (selectedEvent?.value) {
        data += `&event_id=${selectedEvent.value}`;
      }

      const result = await getAll(data);
      const Data = result?.data?.data;
      const totalCount = result?.data?.total_count;

      console.log("DATAIS", Data);
      if (Data) {
        setTicketSalesBreakDown(Data);
        setTicketSalesBreakDownCount(totalCount);

        setTotalSales(Data);
        setTotalSalesCount(totalCount);
      }
    } catch (error) {
      console.error("Error fetching ticket sales breakdown:", error);
    }
  };

  const MetricData = [
    {
      label: "Sessions",
      metric: sessions?.total_sessions || 0,
      icon: "metricUp",
      key: "sessions",
    },
    {
      label: "Revenue",
      // metric:`£ ${revenueAndOrders?.total_revenue || 0}`,
      metric: `£ ${dailySales?.total || 0}`,
      icon: "metricUp",
      key: "revenue",
    },
    {
      label: "Pending Revenue",
      metric: `£ ${pendingRevenue?.total || 0}`,
      icon: "metricUp",
      key: "pendingRevenue",
    },
    {
      label: "Orders",
      metric: salesByDate?.total || 0,
      icon: "metricUp",
      key: "orders",
    },
    {
      label: "Tickets",
      metric: ticketSales?.total || 0,
      icon: "metricUp",
      key: "tickets",
    },
    {
      label: "Conversion Rate",
      metric: conversionRate ? `${conversionRate}%` : '0%',
      icon: "metricUp",
      key: "conversion"
    },
  ];

  return (
    <>
      <div className={styles.container}>
        <div className={styles.containerHeaderContainer}>
          <span className={styles.containerHeader}>Dashboard</span>
          <div className={styles.headerActions}>
            <div className={styles.eventFilterWrapper}>
              <SelectSearch
                id="dashboard-event-filter"
                placeholder="Select Event"
                value={selectedEvent}
                onChange={(val) => setSelectedEvent(val)}
                loadOptions={fetchOptions}
                formGroupStyleOverride={{ width: "30rem", marginBottom: "0" }}
              />
            </div>
            <DateFilterDropdown onDateRangeChange={handleDateRangeChange} />
          </div>
        </div>

        <div className={styles.metrics}>
          <Metric
            data={MetricData}
            selectedMetrics={selectedMetrics}
            onMetricClick={handleMetricClick}
          />
        </div>

        <div className={styles.graphContainer}>
          <ComparisonGraph
            // label="Revenue Over Time"
            // metricNumber={`£${revenueAndOrders?.total_revenue || 0}`}
            showHeader={true}
            showMetricNumber={true}
            showComparison={showComparison}
            onToggleComparison={handleToggleComparison}
            currentData={priceByDate}
            comparisonData={comparisonData}
            comparisonPeriod={comparisonPeriod}
            dashboardOverrideStyles={styles.dashboardOverrideStyles}
            // New props for multi-metric support
            allMetricsData={allMetricsData}
            allMetricsComparisonData={allMetricsComparisonData}
            selectedMetrics={selectedMetrics}
            metricLabels={{
              revenue: "Revenue (£)",
              pendingRevenue: "Pending Revenue (£)",
              sessions: "Sessions",
              orders: "Orders",
              tickets: "Tickets",
              conversion: "Conversion Rate",
            }}
          />
        </div>

        <div className={styles.HomeData}>
          <div className={styles.TotalTicketSales}>
            <div className={styles.DataTableContainer}>
              <div className={styles.PieDiagram}>
                <PieDiagram
                  startDate={currentStartDate}
                  endDate={currentEndDate}
                  dateLabel={dateLabel}
                  eventId={selectedEvent?.value}
                />
              </div>
            </div>
          </div>
          <div className={styles.TotalSales}>
            <div className={styles.TotalSalesHeader}>
              <div className={styles.TotalSalesLabel}>Daily Sales </div>
            </div>
            <DataTable_
              data={totalSales}
              columns={columns}
              handlefetchData={({ limit, offset }) =>
                fetchTicketSalesBreakDown({
                  limit,
                  offset,
                  startDate: currentStartDate,
                  endDate: currentEndDate,
                })
              }
              totalCount={totalSalesCount}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default Home;
